import React, { useState, useEffect } from "react";
import NonPhysicalWitness from "./physicalInterventionForms/non-physical-witness";
import NonPhysicalBehavior from "./physicalInterventionForms/non-physical-behavioural";
import NonPhysicalStrategies from "./physicalInterventionForms/non-physical-strategy";
import NonPhysicalDetail from "./physicalInterventionForms/non-physical-detail";
import AfterIncident from "./physicalInterventionForms/after-incident";
import AntecedentBehaviour from "./physicalInterventionForms/antecedent-behaviour";
import NonPhysicalChallenge from "./physicalInterventionForms/non-physical-challenge";
import NonPhysicalIncident from "./physicalInterventionForms/non-physical-incident";
import IncidentManagement from "./physicalInterventionForms/incident-management";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const NonPhysicalIntervention = ({ taskId }) => {
  const navigate = useNavigate();

  const stepKey = `nonPhysicalStep-${taskId}`;
  const formDataKey = `nonPhysicalFormData-${taskId}`;

  const initialStep = parseInt(localStorage.getItem(stepKey)) || 0;

  const getInitialFormData = () => {
    try {
      const savedData = localStorage.getItem(formDataKey);
      return savedData
        ? JSON.parse(savedData)
        : {
            nonIncidentManagementInfo: {},
            nonPhysicalWitnessInfo: {},
            nonPhysicalBehaviouralInfo: {},
            nonPhysicalStrategyInfo: {},
            nonPhysicalDetailInfo: {},
            afterIncidentInfo: {},
            antecedentBehaviourInfo: {},
            nonPhysicalChallengeInfo: {},
            nonPhysicalIncidentInfo: {},
          };
    } catch (e) {
      return {};
    }
  };

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState(getInitialFormData);

  useEffect(() => {
    localStorage.setItem(formDataKey, JSON.stringify(formData));
    localStorage.setItem(stepKey, currentStep.toString());
  }, [formData, currentStep]);

  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const updateFormData = (newData) => {
    setFormData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const updateDailyLogStatus = async () => {
    try {
      await axios.put(`/api/dailyLog/log/${taskId}`, {
        status: "completed",
      });
    } catch (error) {
      console.error("Error updating daily log status:", error);
      alert("Failed to update daily log status.");
    }
  };

  const handleSubmit = async () => {
    try {
      await axios.post(
        "/api/nonPhysical/nonPhysicalIncident",
        { ...formData, dailyLogId: taskId },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      await updateDailyLogStatus();

      localStorage.removeItem(formDataKey);
      localStorage.removeItem(stepKey);

      navigate("/daily-logs");
    } catch (err) {
      console.error("Submission error:", err.response?.data?.error || err.message);
      alert("Submission failed.");
    }
  };

  const renderForm = () => {
    switch (currentStep) {
      case 0:
        return (
          <IncidentManagement
            formData={formData.nonIncidentManagementInfo}
            updateFormData={updateFormData}
            nextStep={nextStep}
          />
        );
      case 1:
        return (
          <NonPhysicalWitness
            formData={formData.nonPhysicalWitnessInfo}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 2:
        return (
          <NonPhysicalBehavior
            formData={formData.nonPhysicalBehaviouralInfo}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 3:
        return (
          <NonPhysicalStrategies
            formData={formData.nonPhysicalStrategyInfo}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 4:
        return (
          <NonPhysicalDetail
            formData={formData.nonPhysicalDetailInfo}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 5:
        return (
          <AfterIncident
            formData={formData.afterIncidentInfo}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 6:
        return (
          <AntecedentBehaviour
            formData={formData.antecedentBehaviourInfo}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 7:
        return (
          <NonPhysicalChallenge
            formData={formData.nonPhysicalChallengeInfo}
            temp={formData.temperature}
            person={formData.whoWasAround}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 8:
        return (
          <NonPhysicalIncident
            formData={formData.nonPhysicalIncidentInfo}
            category={formData.incidentCategory}
            updateFormData={updateFormData}
            prevStep={prevStep}
            handleSubmit={handleSubmit}
          />
        );
      default:
        return <div>Unknown Step</div>;
    }
  };

  return <div>{renderForm()}</div>;
};

export default NonPhysicalIntervention;
